import React from 'react';
import { FileText, Download, Calendar, ArrowLeft, ExternalLink, Award, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DOCUMENTS } from '../data';

export default function Minutes() {
  return (
    <div className="bg-slate-50 min-h-screen py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-slate-700 transition">
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
        </div>

        {/* Header */}
        <div className="border-b border-slate-200 pb-8 mb-12">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Mødereferater & Årsrapporter
          </h1>
          <p className="mt-3 text-lg text-slate-500 max-w-3xl">
            Her kan du læse referater fra vores årlige generalforsamlinger, formandens beretninger samt hente de godkendte årsrapporter og regnskaber.
          </p>
        </div>

        {/* General Assembly Section */}
        <section className="mb-16">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="h-1.5 w-8 rounded-full bg-slate-900" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-900">Generalforsamling 2026</span>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-6 border-b border-slate-100">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900">
                  <Calendar className="h-3.5 w-3.5" />
                  Afholdt 26. februar 2026
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">Generalforsamling 2026</h3>
                <p className="text-sm text-slate-500">
                  Den årlige generalforsamling blev afholdt i overensstemmelse med vedtægterne. Find referat og beretning herunder.
                </p>
              </div>
              <div className="shrink-0 flex gap-3">
                <a
                  href={DOCUMENTS.generalAssembly.referat.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-500 transition-all shadow-sm"
                >
                  <Download className="h-3.5 w-3.5" />
                  Hent Referat (PDF)
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
              {/* Referat Card */}
              <div className="flex gap-4 items-start">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center text-slate-900">
                  <FileText className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-900">{DOCUMENTS.generalAssembly.referat.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {DOCUMENTS.generalAssembly.referat.description} Indeholder dagsorden, valg til bestyrelsen og godkendelse af regnskab.
                  </p>
                  <a
                    href={DOCUMENTS.generalAssembly.referat.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline pt-1"
                  >
                    Læs referat online
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>

              {/* Beretning Card */}
              <div className="flex gap-4 items-start">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                  <Award className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-900">{DOCUMENTS.generalAssembly.beretning.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {DOCUMENTS.generalAssembly.beretning.description} Formandens mundtlige beretning om årets drift, renoveringer og fremtidsplaner.
                  </p>
                  <a
                    href={DOCUMENTS.generalAssembly.beretning.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline pt-1"
                  >
                    Læs beretning online
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Annual Reports Section */}
        <section>
          <div className="flex items-center gap-2.5 mb-6">
            <div className="h-1.5 w-8 rounded-full bg-slate-900" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-900">Årsrapporter & Regnskaber</span>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <div className="max-w-3xl mb-8">
              <h3 className="text-xl font-extrabold text-slate-900">Historiske Årsrapporter</h3>
              <p className="text-sm text-slate-500 mt-1">
                Herunder finder du de godkendte årsrapporter og regnskaber for de seneste år. Alle rapporter er i PDF-format og klar til download.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {DOCUMENTS.annualReports.map((report) => (
                <div
                  key={report.year}
                  className="flex items-center justify-between p-4 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-blue-200 hover:shadow-sm transition duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center text-slate-900">
                      <TrendingUp className="h-5 w-5" />
                    </div>
                    <div>
                      <span className="block font-bold text-slate-900">{report.title}</span>
                      <span className="block text-xs text-slate-400">Årsregnskab for {report.year}</span>
                    </div>
                  </div>
                  <a
                    href={report.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:shadow-xs transition"
                    title={`Hent ${report.title}`}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
